import type { TypedDocumentNode } from '@apollo/client'
import { gql, NetworkStatus } from '@apollo/client'
import { QueryRef, useApolloClient, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, LocationCard } from '@repo/shared-client/components/blocks'
import { STATIC_MINIMAP_RANGE_KM } from '@repo/shared-client/mapbox'
import { createFileRoute } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import Posts from '@web/src/features/post/posts/posts'
import { useAuth } from '@web/src/hooks/use-auth'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import ProfileDetailsCard from 'src/features/profile/profile-details-card/profile-details-card'
import ProfileHeader from 'src/features/profile/profile-header/profile-header'
import ProfileMenu from 'src/features/profile/profile-menu/profile-menu'
import ProfileVerification from 'src/features/profile/profile-verification/profile-verification'
import { PartnerInfo, ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type { ProfilePostsQuery, ProfilePostsQueryVariables } from 'types/graphql'

const PROFILE_POSTS_QUERY: TypedDocumentNode<ProfilePostsQuery, ProfilePostsQueryVariables> = gql`
  query ProfilePostsQuery($profileId: String!) {
    isBlocked(profileId: $profileId)
    isFriend(profileId: $profileId) {
      id
      friendId
      approved
    }
    isFollowing(profileId: $profileId) {
      id
      followingId
    }
    friendsCounts(filter: { profileId: $profileId }) {
      friends
      pending
      requests
    }
    profile(id: $profileId) {
      ...ProfileInfo
      partners {
        ...PartnerInfo
      }
    }
  }
  ${PartnerInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId_/posts/')({
  component: PostsPage,
  head: () =>
    seo({
      title: `Profile Posts | ${env.VITE_APP_NAME}`,
      description: 'Profile posts page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, PROFILE_POSTS_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function PostsPage() {
  const { t } = useTranslation()
  const { hasPermission } = useAuth()
  const { mapBoxToken } = useMapBox()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<ProfilePostsQuery>)

  const handleRefetch = async () => {
    await apolloClient.refetchQueries({ include: [PROFILE_POSTS_QUERY] })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!data?.profile) {
    return <ErrorAlert error={{ name: 'Empty', message: t('Profile Not Found') }} />
  }

  const { profile, isBlocked, isFriend, isFollowing } = data

  const canViewMap = hasPermission('location:view-map') && Boolean(mapBoxToken)
  const distanceRaw = profile.distance
  const distanceKm = distanceRaw != null && Number.isFinite(distanceRaw) ? Math.round(distanceRaw) : 0
  const locationDisplayText = t('Near {{location}}', { location: profile.location ?? '' })
  const latitude = profile.locationLat ?? 0
  const longitude = profile.locationLong ?? 0
  const mapRangeKm = canViewMap ? STATIC_MINIMAP_RANGE_KM : undefined
  const mapToken = mapBoxToken ?? ''

  return (
    <div className="mx-auto w-full max-w-7xl px-0">
      <ProfileHeader
        isBlocked={isBlocked}
        isFollowing={isFollowing}
        isFriend={isFriend}
        onQueryRefetch={handleRefetch}
        profile={profile}
      />
      <div className="mb-4 grid grid-cols-2 gap-3 px-2 md:grid-cols-4">
        <div className="col-span-2 hidden md:col-span-1 md:mt-6 md:flex md:w-full md:justify-center">
          <ProfileMenu
            className="hidden w-[80%] max-w-full md:flex"
            isBlocked={isBlocked}
            onQueryRefetch={handleRefetch}
            profile={profile}
          />
        </div>
        <div className="col-span-2 mt-10 md:col-span-2 md:mt-0">
          <Posts filterType="PROFILE" noSideBars={true} profileId={profile.id} showSort={false} />
        </div>
        <div className="col-span-1">
          <div className="hidden flex-col items-stretch gap-3 md:flex">
            <ProfileDetailsCard profile={profile} />
            <ProfileVerification profileId={profile?.id} />
            <LocationCard
              className="min-w-[300px]"
              distanceKm={distanceKm}
              latitude={latitude}
              location={locationDisplayText}
              longitude={longitude}
              mapboxToken={mapToken}
              rangeKm={mapRangeKm}
              showSupporterPlaceholder={!canViewMap}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
